import { useCallback, useRef } from 'react';
import { styled } from 'styled-components';

import { Button } from '#/components/Button/Button';
import { Image } from '#/components/Image/Image';
import { Body } from '#/components/Text/Body';
import { H2 } from '#/components/Text/H2';
import { useElementReachedEffect } from '#/hooks/viewport/useElementReachedEffect';
import { useI18n } from '#/i18n/hooks/useI18n';
import { colors, gradients } from '#/theme/theme';
import type { ListingType } from '#/types/ListingType';
import { trackEventOnce } from '#/utils/analytics/track/trackEventOnce';
import { LISTING_TYPE_HUMAN_TRACKING_NAME_MAP_TITLE } from '#/utils/constants/general/listingTypeHumanNameMapTitle';
import { getStaticImageSrc } from '#/utils/getImageSrc';

import {
  ListingViewSignupCta_Box_1,
  ListingViewSignupCta_Box_2,
  ListingViewSignupCta_Hide_1,
} from './ListingViewSignupCta.styled';

const Tile = styled.div`
  ${gradients.skyBlueToBrandBlueTile};
  box-shadow: 0 2px 4px rgb(0 0 0 / 0.1);
  background: ${colors.white};
  position: relative;
  display: flex;
  flex: 1;
  padding-block: 64px;
  padding-inline: 32px;
  border-radius: 4px;
`;

type Props = {
  listingUrl: string;
  type: ListingType;
};

export function ListingViewSignupCta({ listingUrl, type }: Props) {
  const { i18n } = useI18n();
  const elementReachedRef = useRef<HTMLDivElement>(null);

  useElementReachedEffect({
    ref: elementReachedRef,
    threshold: 0.5,
    onReach: useCallback(() => {
      trackEventOnce('Displayed: Job Listing CTA block');
    }, []),
  });

  return (
    <div data-qa-id="job-listing-signup-promo" ref={elementReachedRef}>
      <Tile>
        <ListingViewSignupCta_Box_2>
          <ListingViewSignupCta_Hide_1>
            <Image
              src={getStaticImageSrc('planning.svg')}
              alt="Illustration"
              expandSize
            />
          </ListingViewSignupCta_Hide_1>
        </ListingViewSignupCta_Box_2>
        <ListingViewSignupCta_Box_1>
          <H2 style={{ marginBottom: 3 }}>
            {type === 'JOB'
              ? i18n('Take the Next Step in Your Career')
              : i18n('Discover Your Calling')}
          </H2>
          <Body style={{ marginBottom: 3 }}>
            {type === 'JOB'
              ? i18n(
                  'Match with social-impact hiring managers, explore the latest job opportunities, and get notified when new opportunities meet your search criteria.',
                )
              : i18n(
                  'Find opportunities to change the world with the latest social-impact job, internship, and volunteer listings. Plus, explore resources for taking action in your community.',
                )}
          </Body>

          <div style={{ marginBottom: 16 }}>
            <Button
              type="button"
              variant="primary"
              clickActions={{
                track: {
                  name: `${LISTING_TYPE_HUMAN_TRACKING_NAME_MAP_TITLE[type]} Listing CTA: Sign Up`,
                },
                modal: { type: 'SIGNUP', props: { redirectTo: listingUrl } },
              }}
              qaId="listing-promo-signup-link"
            >
              {i18n('Sign Up Now')}
            </Button>
          </div>

          <Button
            type="button"
            variant="link"
            size="small"
            noPadding
            clickActions={{
              track: {
                name: `${LISTING_TYPE_HUMAN_TRACKING_NAME_MAP_TITLE[type]} Listing CTA: Log In`,
              },
              modal: {
                type: 'LOGIN',
                props: { redirectTo: listingUrl },
              },
            }}
            qaId="listing-promo-login-link"
          >
            {i18n('Already a user? Log in')}
          </Button>
        </ListingViewSignupCta_Box_1>
      </Tile>
    </div>
  );
}
